import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';

import {
  EditTableauPageComponent
} from '@components/pages/page-components/tableau-page/modal/edit-tableau-page/edit-tableau-page.component';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {TableauPage} from '@models/sidebar/response/tableau-page';
import {TableauPageConfigurationService} from '@services/pages/tableau-page/tableau-page-configuration.service';
import {Machine} from '@models/machine/machine';
import {cloneDeep} from 'lodash';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {ModalService} from '@services/modal/modal.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'scitis-tableau-page-actions',
    templateUrl: './tableau-page-actions.component.html',
    styleUrls: ['./tableau-page-actions.component.scss'],
    standalone: false
})
export class TableauPageActionsComponent implements OnDestroy {

  @Input()
  public machineOrGroupId: string = '';
  @Input()
  public pageParentType: MenuItemType = MenuItemType.NONE;
  @Input()
  public machine: Machine = new Machine();
  @Input()
  public isAdmin: boolean = false;
  @Input()
  public isLowerThanAdmin: boolean = false;
  @Input()
  public tableauPageConfiguration: TableauPage = new TableauPage('id', 'name', !this.isUnderMachineGroup);
  @Input()
  public allDataIsLoaded = false;

  @Output()
  public tableauPageConfigurationChange: EventEmitter<TableauPage> = new EventEmitter<TableauPage>();

  private readonly COMPONENT_NAME: string = 'TableauPageActionsComponent';
  private $destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private modalService: ModalService,
              private tableauPageConfigurationService: TableauPageConfigurationService,
              private translateService: TranslateService,
              private notificationService: NotificationService) {
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  public get isUnderMachineGroup(): boolean {
    return this.pageParentType === MenuItemType.MACHINE_GROUP;
  }

  public onEditPage(): void {
    if (!this.machine.tableauProjectId && this.pageParentType !== MenuItemType.MACHINE_GROUP || this.machine.tableauProjectId === '-1') {
      this.notificationService.warning(
        this.translateService.instant('No tableau project is configured.'),
        this.COMPONENT_NAME
      );
      return;
    }
    const tableauPageBackup = cloneDeep(this.tableauPageConfiguration);
    const modalRef = this.modalService.open(EditTableauPageComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.pageParentType = this.pageParentType;
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.tableauPageConfiguration = this.tableauPageConfiguration;


    modalRef.result.then((result: TableauPage) => {
      this.saveTableauPageConfiguration(tableauPageBackup, result);

    }, (reason: ModalDialogDismissOption | any) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        this.tableauPageConfigurationChange.emit(tableauPageBackup);
      }
    });
  }


  private saveTableauPageConfiguration(backupPage: TableauPage, tableauPage: TableauPage): void {
    this.tableauPageConfigurationService.editTableauView(this.machineOrGroupId, tableauPage)
      .pipe(takeUntil(this.$destroy)).subscribe({
        next: (tableauView) => {
          const message = this.translateService.instant('Saved Tableau page configuration.');
          this.notificationService.success(message, this.COMPONENT_NAME, false);
          this.tableauPageConfigurationChange.emit(tableauView);
        }, error: (error) => {
          const message = this.translateService.instant('There was an error saving the tableau page configuration.');
          this.notificationService.error(message, error, this.COMPONENT_NAME, true);
          this.tableauPageConfigurationChange.emit(backupPage);
          console.log(error);
        }
      });
  }

  protected readonly MenuItemType = MenuItemType;
}
